import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { Button } from "primereact/button";
import { Password } from "primereact/password";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import {
  useLazyGetUserListQuery,
  useUpdateUserPasswordMutation,
} from "../../../../redux/services/user";

import * as styles from "../UserManagement.module.css";

const LIST_PATH = "/app/admin/users";

function UserPasswordPage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const orgSubdomain = searchParams.get("org");

  const [fetchUsers, { data: users }] = useLazyGetUserListQuery();

  useEffect(() => {
    if (orgSubdomain) fetchUsers(orgSubdomain);
  }, [orgSubdomain, fetchUsers]);

  const user = useMemo(
    () => users?.find((u) => String(u.id) === String(id)),
    [users, id],
  );

  const { control, handleSubmit } = useForm({
    defaultValues: { password: "" },
  });

  const [
    updateUserPassword,
    {
      isSuccess: isUpdateUserPasswordSuccess,
      isError: isUpdateUserPasswordError,
      error: updateUserPasswordError,
    },
  ] = useUpdateUserPasswordMutation();

  useEffect(() => {
    if (isUpdateUserPasswordSuccess) {
      showToast("success", "User", "User password updated!");
      navigate(LIST_PATH);
    }
  }, [isUpdateUserPasswordSuccess]);

  useEffect(() => {
    if (isUpdateUserPasswordError) {
      showToast(
        "error",
        updateUserPasswordError?.status,
        updateUserPasswordError?.data?.message ||
          (updateUserPasswordError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateUserPasswordError]);

  const onSavePassword = (data) => {
    if (!orgSubdomain) {
      showToast("warn", "Warning", "Organization is missing");
      return;
    }
    updateUserPassword({
      subdomain: orgSubdomain,
      payload: { id: Number(id), password: data.password },
    });
  };

  const title = user?.name
    ? `Edit User Password — ${user.name}`
    : "Edit User Password";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onSavePassword)}
        />
      }
    >
      <form onSubmit={handleSubmit(onSavePassword)}>
        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Password</label>
              <Password
                className={`w-full ${styles.passwordInput}`}
                autoComplete="new-password"
                type="password"
                placeholder="Password"
                toggleMask
                feedback={false}
                {...field}
              />
            </div>
          )}
        />
        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default UserPasswordPage;
